import { FAILURE_REASONS } from '../constants/failed';
import images from '../constants/images';
import { ORGANIZATIONS } from '../constants/organizations';
import SESSION_STORAGE_KEY from '../constants/sessionStorageKey';
import LOCAL_STORAGE_KEY from '../constants/localStorageKey';
import STORAGE_KEYS from '../constants/storage';
import { DEEPLINK_PARAMS } from '../constants/deeplinkParams';

const convertFiletoBase64 = (file: File) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
	});

const formatPrice = (value?: number) => {
	if (value === null || value === undefined) return '-';
	const rounded = Math.round(value / 100);
	return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatCurrency = (value: number | null, showCurrency: boolean = true, options?: {
	decimals?: number;
	round?: 'floor' | 'ceil' | 'round';
	isInCents?: boolean;
}) => {
	if (typeof value !== 'number' || value === null || value === undefined)
		return '-';

	const decimals = options?.decimals ?? 2;
	const round = options?.round ?? 'round';
	const isInCents = options?.isInCents ?? false;
	
	const finalValue = isInCents ? value / 100 : value;
	
	if (!round) {
		return new Intl.NumberFormat('es-MX', {
			style: showCurrency ? 'currency' : 'decimal',
			currency: 'MXN',
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals
		}).format(finalValue);
	}

	let roundedValue = finalValue;
	switch (round) {
		case 'floor':
			roundedValue = Math.floor(finalValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
			break;
		case 'ceil':
			roundedValue = Math.ceil(finalValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
			break;
		case 'round':
		default:
			roundedValue = Math.round(finalValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
	}

	return new Intl.NumberFormat('es-MX', {
		style: showCurrency ? 'currency' : 'decimal',
		currency: 'MXN',
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals
	}).format(roundedValue);
};

const getRecurrency = (value: number) => {
	switch (value) {
		case 30: {
			return 'Mensual';
		}
		case 60: {
			return 'Bimestral';
		}
		case 90: {
			return 'Trimestral';
		}
		case 180: {
			return 'Semestral';
		}
		case 365: {
			return 'Anual';
		}
		default: {
			return '';
		}
	}
};
const capitalizeFirstLetter = (string: string) =>
	string?.charAt(0)?.toUpperCase() + string?.slice(1)?.toLowerCase();

const capitalizeText = (text: string) => {
	return text
		?.split(' ')
		?.map((word) => capitalizeFirstLetter(word))
		?.join(' ');
};

const getFailuresText = (failure: string) => {
	if (failure === FAILURE_REASONS.INSUFFICIENT_FUNDS) {
		return 'Tu suscripción se encuentra en pausa debido a que tienes fondos insuficientes, ';
	} else {
		return 'Tu suscripción se encuentra en pausa debido a que no pudimos realizar la renovación, ';
	}
};

const getBrowser = () => {
	if (
		(navigator.userAgent.indexOf('Opera') ||
			navigator.userAgent.indexOf('OPR')) != -1
	) {
		return 'Opera';
	} else if (navigator.userAgent.indexOf('Edg') != -1) {
		return 'Edge';
	} else if (navigator.userAgent.indexOf('Chrome') != -1) {
		return 'Chrome';
	} else if (navigator.userAgent.indexOf('Safari') != -1) {
		return 'Safari';
	} else if (navigator.userAgent.indexOf('Firefox') != -1) {
		return 'Firefox';
	} else if (
		navigator.userAgent.indexOf('MSIE') != -1 ||
		!!document?.DOCUMENT_NODE == true
	) {
		//IF IE > 10
		return 'IE';
	} else {
		return 'unknown';
	}
};

const getBgMobileByOrg = (org: string) => {
	if (org === ORGANIZATIONS.UBER_EATS) {
		return images.bgUberEatsMobile;
	} else if (org === ORGANIZATIONS.UBER) {
		return images.bgUberMobile;
	} else if (org === ORGANIZATIONS.AIRBNB) {
		return images.bgAirbnbMobile;
	} else if (org === ORGANIZATIONS.CORNERSHOP) {
		return images.bgCornershopMobile;
	} else if (org === ORGANIZATIONS.DIDI) {
		return images.bgDidiMobile;
	} else if (org === ORGANIZATIONS.RAPPI) {
		return images.bgRappiMobile;
	} else if (org === ORGANIZATIONS.MERCADO_ENVIOS) {
		return images.bgMercadoEnviosMobile;
	} else {
		return '';
	}
};

const getBgDesktopByOrg = (org: string) => {
	if (org === ORGANIZATIONS.UBER_EATS) {
		return images.bgUberEatsDesktop;
	} else if (org === ORGANIZATIONS.UBER) {
		return images.bgUberDesktop;
	} else if (org === ORGANIZATIONS.AIRBNB) {
		return images.bgAirbnbDesktop;
	} else if (org === ORGANIZATIONS.CORNERSHOP) {
		return images.bgCornershopDesktop;
	} else if (org === ORGANIZATIONS.DIDI) {
		return images.bgDidiDesktop;
	} else if (org === ORGANIZATIONS.RAPPI) {
		return images.bgRappiDesktop;
	} else if (org === ORGANIZATIONS.MERCADO_ENVIOS) {
		return images.bgMercadoEnviosDesktop;
	} else {
		return '';
	}
};

const getBorderColorByOrg = (org: string) => {
	switch (org) {
		case ORGANIZATIONS.RAPPI:
			return 'border-h-error-70';
		case ORGANIZATIONS.DIDI:
			return 'border-orange-300';
		case ORGANIZATIONS.AIRBNB:
			return 'border-red-300';
		case ORGANIZATIONS.UBER:
			return 'border-h-gray-100';
		case ORGANIZATIONS.CORNERSHOP:
			return 'border-orange-600';
		case ORGANIZATIONS.MERCADO_ENVIOS:
			return 'border-lime-400';
		default:
			return 'border-h-green-40';
	}
};

const getCountryFlag = (shortName: string) => {
	switch (shortName) {
		case 'MX':
			return images.flagMx;
		case 'US':
			return images.flagUs;
		case 'CO':
			return images.flagCo;
		case 'PE':
			return images.flagPe;
		case 'GT':
			return images.flagGt;
		case 'AR':
			return images.flagAr;
		case 'UY':
			return images.flagUy;
		case 'CL':
			return images.flagCl;
		case 'GB':
			return images.flagGb;
		case 'ES':
			return images.flagEs;
		default:
			return '';
	}
};

const isLocalStorageEnabled = () => {
	try {
		const key = `__storage__test`;
		window.localStorage.setItem(key, 'null');
		window.localStorage.removeItem(key);
		return true;
	} catch (e) {
		return false;
	}
};

const isSessionStorageEnabled = () => {
	try {
		const key = `__storage__test`;
		window.sessionStorage.setItem(key, 'null');
		window.sessionStorage.removeItem(key);
		return true;
	} catch (e) {
		return false;
	}
};

const getStorage = () => {
	const storage = isLocalStorageEnabled() ? localStorage : sessionStorage;
	return {
		getItem: (key: string) => storage.getItem(key),
		setItem: (key: string, value: string) => storage.setItem(key, value),
		removeItem: (key: string) => storage.removeItem(key),
	};
};

const getPlatformOverride = () =>
	getStorage().getItem(LOCAL_STORAGE_KEY.DEBUG_PLATFORM_OVERRIDE) as 'app_native' | 'app_pwa' | 'app' | 'web' | null;

const getIsStandalone = () => {
	const override = getPlatformOverride();
	if (override) return override === 'app_pwa';
	return (
		window.matchMedia('(display-mode: standalone)').matches ||
		('standalone' in navigator && (navigator as unknown as { standalone: boolean }).standalone) ||
		document.referrer.includes('android-app://') ||
		new URLSearchParams(window.location.search).get('viewed_from') === 'app'
	);
};

// The react-native-webview runtime always exposes this bridge object inside
// the app shell's webview — every binary, old or new, param or no param. It is
// the strongest "we are inside the native app" signal and covers the legacy
// binaries that never appended `viewed_from=app` to their URLs.
const isEmbeddedInNativeWebView = (): boolean =>
	typeof window !== 'undefined' &&
	typeof (window as { ReactNativeWebView?: unknown }).ReactNativeWebView !==
		'undefined';

const getIsNative = () => {
	const override = getPlatformOverride();
	if (override) return override === 'app_native';
	if (getStorage().getItem(SESSION_STORAGE_KEY.VIEWED_FROM_APP) === 'true') {
		return true;
	}
	// Late detection, two signals:
	//  - LIVE `?viewed_from=app`: the app shell navigates its webview via
	//    injected pushState (no full reload), so the boot capture misses the
	//    param it appends on every navigation.
	//  - The react-native-webview bridge object: present in EVERY app binary,
	//    including legacy ones that never sent the param at all.
	// Either one persists the marker so the rest of the session (and, via
	// localStorage, future ones in the webview's dedicated profile) stays
	// attributed as native.
	const liveFromApp =
		new URLSearchParams(window.location.search).get(
			DEEPLINK_PARAMS.VIEWED_FROM,
		) === 'app' || isEmbeddedInNativeWebView();
	if (liveFromApp) {
		try {
			getStorage().setItem(SESSION_STORAGE_KEY.VIEWED_FROM_APP, 'true');
		} catch {
			// storage unavailable — the live signal still attributes this event
		}
	}
	return liveFromApp;
};

const getEventSource = (isMobile: boolean): string => {
	const override = getPlatformOverride();
	if (override) return override;
	if (getIsNative()) return 'app_native';
	if (getIsStandalone()) return 'app_pwa';
	return isMobile ? 'app' : 'web';
};

const getViewedFrom = (): 'pwa' | 'browser' =>
	getIsStandalone() ? 'pwa' : 'browser';

// Single reader for the platform-os value. The live URL param wins (it just
// arrived from the native webview / app-style launch), falling back to the
// value `capturePlatformFromUrl` persisted to storage at boot. Callers apply
// their own default. Use this instead of reading `?platform_os` off the URL
// directly.
const getPlatformOs = (): string | null => {
	const fromUrl = new URLSearchParams(window.location.search).get(
		DEEPLINK_PARAMS.PLATFORM_OS,
	);
	if (fromUrl) return fromUrl;
	return getStorage().getItem(STORAGE_KEYS.PLATFORM_OS);
};

// Persists platform-identification params from native webviews / app-style
// launches. Called once at boot (main.tsx), before first render, so the
// values are available immediately and survive the /login bounce — same
// pattern as the UTM and checkout-intent captures.
const capturePlatformFromUrl = (): void => {
	const params = new URLSearchParams(window.location.search);
	const platformOS = params.get(DEEPLINK_PARAMS.PLATFORM_OS);
	if (platformOS) {
		getStorage().setItem(STORAGE_KEYS.PLATFORM_OS, platformOS);
	}
	if (params.get(DEEPLINK_PARAMS.VIEWED_FROM) === 'app') {
		getStorage().setItem(SESSION_STORAGE_KEY.VIEWED_FROM_APP, 'true');
	}
};

export {
	convertFiletoBase64,
	formatCurrency,
	formatPrice,
	getRecurrency,
	capitalizeFirstLetter,
	getFailuresText,
	getBrowser,
	getBgMobileByOrg,
	getBgDesktopByOrg,
	getBorderColorByOrg,
	capitalizeText,
	getCountryFlag,
	isLocalStorageEnabled,
	isSessionStorageEnabled,
	getStorage,
	getEventSource,
	getViewedFrom,
	getIsStandalone,
	getIsNative,
	getPlatformOs,
	capturePlatformFromUrl,
};
